// src/components/assistant/SheetsChat.tsx
// Sheets mode chat - Two-way AI copilot for spreadsheet generation and analysis

import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Table2,
  Check,
  X,
  Eye,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  FileSpreadsheet,
  Plus,
  Trash2,
  Edit3,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Lightbulb,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ChatComposer from "./ChatComposer";
import ChatEmptyState from "./ChatEmptyState";
import { useSheetsMode } from "@/hooks/assistant/useSheetsMode";
import type { ChatMessage } from "@/services/assistant/AssistantService";
import type { SheetGenerationSpec } from "@/services/sheets/SheetsAIService";
import type {
  SheetAction,
  SheetAIResponse,
  AnalyzeSheetAction,
  EditCellsAction,
  AddColumnsAction,
  AddRowsAction,
  DeleteRowsAction,
  DeleteColumnsAction,
} from "@/services/sheets/SheetActions";
import { isModifyingAction } from "@/services/sheets/SheetActions";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface SheetsChatProps {
  prefill?: string | undefined;
  onPrefillUsed?: () => void;
  compact?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sheet Context Banner
// ─────────────────────────────────────────────────────────────────────────────

interface SheetContextBannerProps {
  sheetName: string;
  rowCount: number;
  colCount: number;
  onRefresh: () => void;
}

const SheetContextBanner: React.FC<SheetContextBannerProps> = ({
  sheetName,
  rowCount,
  colCount,
  onRefresh,
}) => (
  <div className="flex items-center justify-between px-3 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg mb-3">
    <div className="flex items-center gap-2">
      <FileSpreadsheet className="h-4 w-4 text-cyan-400" />
      <span className="text-sm text-white font-medium">{sheetName}</span>
      <span className="text-xs text-gray-400">
        {rowCount} rows × {colCount} cols
      </span>
    </div>
    <button
      onClick={onRefresh}
      className="p-1 text-gray-400 hover:text-white transition-colors"
      title="Refresh context"
    >
      <RefreshCw className="h-3.5 w-3.5" />
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Message Row
// ─────────────────────────────────────────────────────────────────────────────

interface MessageRowProps {
  message: ChatMessage;
  showAvatar: boolean;
}

const MessageRow: React.FC<MessageRowProps> = ({ message, showAvatar }) => {
  const isUser = message.role === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      {/* Assistant Avatar */}
      {!isUser && (
        <div className={`mr-2 mt-1 flex-shrink-0 ${showAvatar ? "opacity-100" : "opacity-0"}`}>
          <div className="h-7 w-7 rounded-lg bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
            <Table2 className="h-3.5 w-3.5 text-cyan-400" />
          </div>
        </div>
      )}

      {/* Message Content */}
      <div className={`${isUser ? "max-w-[80%]" : "max-w-[90%]"}`}>
        <div
          className={`
            rounded-xl shadow-sm text-sm
            ${isUser
              ? "bg-cyan-500/20 text-white px-3 py-2 border border-cyan-500/30"
              : "bg-gray-800/50 text-gray-100 px-3 py-2"
            }
          `}
        >
          <div className="whitespace-pre-wrap break-words leading-relaxed">
            {message.content}
          </div>
        </div>
      </div>

      {/* User Avatar */}
      {isUser && (
        <div className={`ml-2 mt-1 flex-shrink-0 ${showAvatar ? "opacity-100" : "opacity-0"}`}>
          <div className="h-7 w-7 rounded-lg bg-cyan-500/30 flex items-center justify-center border border-cyan-500/40">
            <span className="text-[10px] font-semibold text-cyan-300">You</span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Analysis Result Card
// ─────────────────────────────────────────────────────────────────────────────

interface AnalysisResultCardProps {
  action: AnalyzeSheetAction;
}

const AnalysisResultCard: React.FC<AnalysisResultCardProps> = ({ action }) => {
  const [expanded, setExpanded] = React.useState(true);
  const { summary, insights, columnExplanations, keyMetrics, dataQuality, recommendations } = action.analysis;

  const qualityColors = {
    good: "text-emerald-400 bg-emerald-500/20",
    fair: "text-amber-400 bg-amber-500/20",
    needs_attention: "text-rose-400 bg-rose-500/20",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/80 rounded-xl border border-violet-500/30 overflow-hidden"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-violet-500/10 border-b border-violet-500/20"
      >
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-violet-400" />
          <span className="font-medium text-white">Analysis Results</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${qualityColors[dataQuality]}`}>
            {dataQuality === "needs_attention" ? "Needs Attention" : dataQuality}
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {/* Summary */}
          <p className="text-sm text-gray-300">{summary}</p>

          {/* Insights */}
          {insights.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                Key Insights
              </h4>
              <ul className="space-y-1.5">
                {insights.map((insight: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                    <span className="text-violet-400 mt-1">•</span>
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Column Explanations */}
          {columnExplanations && columnExplanations.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                Column Details
              </h4>
              <div className="space-y-2">
                {columnExplanations.map((col: { column: string; meaning: string; interpretation?: string }, idx: number) => (
                  <div key={idx} className="px-3 py-2 bg-gray-900/50 rounded-lg">
                    <div className="font-medium text-sm text-white">{col.column}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{col.meaning}</div>
                    {col.interpretation && (
                      <div className="text-xs text-cyan-400/70 mt-0.5">{col.interpretation}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Metrics */}
          {keyMetrics && keyMetrics.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                Key Metrics
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {keyMetrics.map((metric: { name: string; value: string; interpretation: string }, idx: number) => (
                  <div key={idx} className="px-3 py-2 bg-gray-900/50 rounded-lg">
                    <div className="text-xs text-gray-400">{metric.name}</div>
                    <div className="font-medium text-white">{metric.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{metric.interpretation}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {recommendations && recommendations.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">
                Recommendations
              </h4>
              <ul className="space-y-1.5">
                {recommendations.map((rec: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                    <Lightbulb className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Edit Preview Card
// ─────────────────────────────────────────────────────────────────────────────

interface EditPreviewCardProps {
  action: EditCellsAction;
  onConfirm: () => void;
  onSkip: () => void;
}

const EditPreviewCard: React.FC<EditPreviewCardProps> = ({ action, onConfirm, onSkip }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gray-800/80 rounded-xl border border-cyan-500/30 overflow-hidden"
  >
    <div className="px-4 py-3 bg-cyan-500/10 border-b border-cyan-500/20 flex items-center gap-2">
      <Edit3 className="h-4 w-4 text-cyan-400" />
      <span className="font-medium text-white">Cell Edits</span>
      <span className="text-xs text-gray-400">({action.edits.length} changes)</span>
    </div>

    <div className="p-3 max-h-48 overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400">
            <th className="text-left px-2 py-1">Cell</th>
            <th className="text-left px-2 py-1">Old Value</th>
            <th className="text-left px-2 py-1">New Value</th>
            <th className="text-left px-2 py-1">Reason</th>
          </tr>
        </thead>
        <tbody>
          {action.edits.map((edit, idx) => (
            <tr key={idx} className="border-t border-gray-700/50">
              <td className="px-2 py-1.5 text-gray-300">
                R{edit.row + 1}C{edit.col + 1}
              </td>
              <td className="px-2 py-1.5 text-rose-400 line-through">{edit.oldValue || "-"}</td>
              <td className="px-2 py-1.5 text-emerald-400">{edit.newValue}</td>
              <td className="px-2 py-1.5 text-gray-500">{edit.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="px-4 py-3 bg-gray-900/30 border-t border-gray-700/50 flex items-center justify-between">
      <p className="text-xs text-gray-400">{action.summary}</p>
      <div className="flex items-center gap-2">
        <button
          onClick={onSkip}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
        >
          <X className="h-3 w-3" />
          Skip
        </button>
        <button
          onClick={onConfirm}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded hover:bg-cyan-500/30 transition-colors"
        >
          <Check className="h-3 w-3" />
          Apply
        </button>
      </div>
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Add Columns Preview Card
// ─────────────────────────────────────────────────────────────────────────────

interface AddColumnsPreviewCardProps {
  action: AddColumnsAction;
  onConfirm: () => void;
  onSkip: () => void;
}

const AddColumnsPreviewCard: React.FC<AddColumnsPreviewCardProps> = ({
  action,
  onConfirm,
  onSkip,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gray-800/80 rounded-xl border border-emerald-500/30 overflow-hidden"
  >
    <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20 flex items-center gap-2">
      <Plus className="h-4 w-4 text-emerald-400" />
      <span className="font-medium text-white">Add Columns</span>
      <span className="text-xs text-gray-400">({action.columns.length})</span>
    </div>

    <div className="p-3 space-y-2">
      {action.columns.map((col, idx) => (
        <div key={idx} className="px-3 py-2 bg-gray-900/50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white text-sm">{col.header}</span>
            {col.liveMetric && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                LIVE: {col.liveMetric}
              </span>
            )}
            {col.formula && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 border border-violet-500/30">
                Formula
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1">{col.reason}</p>
        </div>
      ))}
    </div>

    <div className="px-4 py-3 bg-gray-900/30 border-t border-gray-700/50 flex items-center justify-between">
      <p className="text-xs text-gray-400">{action.summary}</p>
      <div className="flex items-center gap-2">
        <button
          onClick={onSkip}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
        >
          <X className="h-3 w-3" />
          Skip
        </button>
        <button
          onClick={onConfirm}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded hover:bg-emerald-500/30 transition-colors"
        >
          <Check className="h-3 w-3" />
          Add
        </button>
      </div>
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Add Rows Preview Card
// ─────────────────────────────────────────────────────────────────────────────

interface AddRowsPreviewCardProps {
  action: AddRowsAction;
  onConfirm: () => void;
  onSkip: () => void;
}

const AddRowsPreviewCard: React.FC<AddRowsPreviewCardProps> = ({ action, onConfirm, onSkip }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-gray-800/80 rounded-xl border border-emerald-500/30 overflow-hidden"
  >
    <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20 flex items-center gap-2">
      <Plus className="h-4 w-4 text-emerald-400" />
      <span className="font-medium text-white">Add Rows</span>
      <span className="text-xs text-gray-400">({action.rows.length})</span>
    </div>

    <div className="p-3 space-y-2">
      {action.rows.map((row, idx) => (
        <div key={idx} className="px-3 py-2 bg-gray-900/50 rounded-lg">
          <div className="flex items-center gap-2">
            {row.ticker && (
              <span className="font-mono text-sm text-cyan-400">{row.ticker}</span>
            )}
            <span className="text-xs text-gray-500">
              After row {row.afterRow + 1}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">{row.reason}</p>
          {row.values.some((v) => v !== null) && (
            <div className="flex gap-1 mt-1.5 flex-wrap">
              {row.values.map((val, i) =>
                val !== null ? (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-800 rounded text-gray-300">
                    {val}
                  </span>
                ) : null
              )}
            </div>
          )}
        </div>
      ))}
    </div>

    <div className="px-4 py-3 bg-gray-900/30 border-t border-gray-700/50 flex items-center justify-between">
      <p className="text-xs text-gray-400">{action.summary}</p>
      <div className="flex items-center gap-2">
        <button
          onClick={onSkip}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
        >
          <X className="h-3 w-3" />
          Skip
        </button>
        <button
          onClick={onConfirm}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded hover:bg-emerald-500/30 transition-colors"
        >
          <Check className="h-3 w-3" />
          Add
        </button>
      </div>
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Delete Preview Card
// ─────────────────────────────────────────────────────────────────────────────

interface DeletePreviewCardProps {
  action: DeleteRowsAction | DeleteColumnsAction;
  type: "rows" | "columns";
  onConfirm: () => void;
  onSkip: () => void;
}

const DeletePreviewCard: React.FC<DeletePreviewCardProps> = ({
  action,
  type,
  onConfirm,
  onSkip,
}) => {
  const indices = type === "rows"
    ? (action as DeleteRowsAction).rowIndices
    : (action as DeleteColumnsAction).colIndices;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/80 rounded-xl border border-rose-500/30 overflow-hidden"
    >
      <div className="px-4 py-3 bg-rose-500/10 border-b border-rose-500/20 flex items-center gap-2">
        <Trash2 className="h-4 w-4 text-rose-400" />
        <span className="font-medium text-white">Delete {type}</span>
        <span className="text-xs text-gray-400">({indices.length})</span>
      </div>

      <div className="p-3">
        <p className="text-sm text-gray-300">{action.reason}</p>
        <div className="flex gap-1 mt-2 flex-wrap">
          {indices.map((idx) => (
            <span
              key={idx}
              className="text-xs px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded border border-rose-500/30"
            >
              {type === "rows" ? `Row ${idx + 1}` : `Col ${idx + 1}`}
            </span>
          ))}
        </div>
      </div>

      <div className="px-4 py-3 bg-gray-900/30 border-t border-gray-700/50 flex items-center justify-end gap-2">
        <button
          onClick={onSkip}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
        >
          <X className="h-3 w-3" />
          Skip
        </button>
        <button
          onClick={onConfirm}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded hover:bg-rose-500/30 transition-colors"
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </button>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sheet Preview Card (Legacy - for CREATE_SHEET)
// ─────────────────────────────────────────────────────────────────────────────

interface SheetPreviewCardProps {
  spec: SheetGenerationSpec;
  onConfirm: () => void;
  onDiscard: () => void;
  onViewSheet?: (sheetId: string) => void;
  lastCreatedSheet?: { sheetId: string } | null;
}

const SheetPreviewCard: React.FC<SheetPreviewCardProps> = ({
  spec,
  onConfirm,
  onDiscard,
  onViewSheet,
  lastCreatedSheet,
}) => {
  const liveColumns = spec.columns.filter((c) => c.type === "live").length;
  const formulaColumns = spec.columns.filter((c) => c.type === "formula").length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className="bg-gray-800/80 rounded-xl border border-cyan-500/30 overflow-hidden"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-cyan-500/10 border-b border-cyan-500/20">
        <div className="flex items-center gap-2">
          <Table2 className="h-4 w-4 text-cyan-400" />
          <span className="font-medium text-white">{spec.name}</span>
        </div>
        {spec.description && (
          <p className="text-xs text-gray-400 mt-1">{spec.description}</p>
        )}
      </div>

      {/* Preview Grid */}
      <div className="p-3 max-h-48 overflow-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-700">
              {spec.columns.map((col, idx) => (
                <th
                  key={idx}
                  className="px-2 py-1.5 text-left text-gray-300 font-medium"
                >
                  {col.header}
                  {col.type === "live" && (
                    <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-cyan-500/20 text-cyan-400">
                      LIVE
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {spec.rows.slice(0, 5).map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b border-gray-800/50">
                {row.values.map((value, colIdx) => (
                  <td key={colIdx} className="px-2 py-1.5 text-gray-400">
                    {value !== null ? String(value) : (
                      spec.columns[colIdx]?.type === "live" ? (
                        <span className="text-cyan-400/50 italic">live</span>
                      ) : (
                        <span className="text-gray-500 italic">formula</span>
                      )
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {spec.rows.length > 5 && (
          <p className="text-center text-xs text-gray-500 mt-2">
            +{spec.rows.length - 5} more rows
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="px-4 py-2 bg-gray-900/50 border-t border-gray-700/50 flex items-center gap-4 text-xs text-gray-400">
        <span>{spec.columns.length} columns</span>
        <span>{spec.rows.length} rows</span>
        {liveColumns > 0 && (
          <span className="text-cyan-400">{liveColumns} live data</span>
        )}
        {formulaColumns > 0 && (
          <span className="text-violet-400">{formulaColumns} formulas</span>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 bg-gray-900/30 border-t border-gray-700/50 flex items-center justify-between">
        <button
          onClick={onDiscard}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <X className="h-3.5 w-3.5" />
          Discard
        </button>
        <div className="flex items-center gap-2">
          {lastCreatedSheet && onViewSheet && (
            <button
              onClick={() => onViewSheet(lastCreatedSheet.sheetId)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <Eye className="h-3.5 w-3.5" />
              View Sheet
            </button>
          )}
          <button
            onClick={onConfirm}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/30 transition-colors"
          >
            <Check className="h-3.5 w-3.5" />
            Create Sheet
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Created Sheet Card
// ─────────────────────────────────────────────────────────────────────────────

interface CreatedSheetCardProps {
  sheetId: string;
  sheetName: string;
  onViewSheet: (sheetId: string) => void;
}

const CreatedSheetCard: React.FC<CreatedSheetCardProps> = ({
  sheetId,
  sheetName,
  onViewSheet,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="flex items-center gap-3 px-4 py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl"
    >
      <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
        <Check className="h-4 w-4 text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{sheetName}</p>
        <p className="text-xs text-gray-400">Sheet created successfully</p>
      </div>
      <button
        onClick={() => onViewSheet(sheetId)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Open
      </button>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Action Preview Renderer
// ─────────────────────────────────────────────────────────────────────────────

interface ActionPreviewProps {
  action: SheetAction;
  index: number;
  onExecute: (index: number) => void;
  onSkip: (index: number) => void;
}

const ActionPreview: React.FC<ActionPreviewProps> = ({ action, index, onExecute, onSkip }) => {
  switch (action.type) {
    case "ANALYZE_SHEET":
      return <AnalysisResultCard action={action} />;
    case "EDIT_CELLS":
      return (
        <EditPreviewCard
          action={action}
          onConfirm={() => onExecute(index)}
          onSkip={() => onSkip(index)}
        />
      );
    case "ADD_COLUMNS":
      return (
        <AddColumnsPreviewCard
          action={action}
          onConfirm={() => onExecute(index)}
          onSkip={() => onSkip(index)}
        />
      );
    case "ADD_ROWS":
      return (
        <AddRowsPreviewCard
          action={action}
          onConfirm={() => onExecute(index)}
          onSkip={() => onSkip(index)}
        />
      );
    case "DELETE_ROWS":
      return (
        <DeletePreviewCard
          action={action}
          type="rows"
          onConfirm={() => onExecute(index)}
          onSkip={() => onSkip(index)}
        />
      );
    case "DELETE_COLUMNS":
      return (
        <DeletePreviewCard
          action={action}
          type="columns"
          onConfirm={() => onExecute(index)}
          onSkip={() => onSkip(index)}
        />
      );
    case "TEXT_RESPONSE":
      // Already rendered in message
      return null;
    case "CREATE_SHEET":
      // Handled separately with SheetPreviewCard
      return null;
    default:
      return null;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Pending Actions Card (for batch confirmation)
// ─────────────────────────────────────────────────────────────────────────────

interface PendingActionsCardProps {
  response: SheetAIResponse;
  onConfirmAll: () => void;
  onDiscardAll: () => void;
  onExecute: (index: number) => void;
  onSkip: (index: number) => void;
}

const PendingActionsCard: React.FC<PendingActionsCardProps> = ({
  response,
  onConfirmAll,
  onDiscardAll,
  onExecute,
  onSkip,
}) => {
  const modifyingActions = response.actions.filter(isModifyingAction);
  const hasModifyingActions = modifyingActions.length > 0;

  return (
    <div className="space-y-3">
      {/* Render each action preview */}
      {response.actions.map((action, idx) => (
        <ActionPreview
          key={idx}
          action={action}
          index={idx}
          onExecute={onExecute}
          onSkip={onSkip}
        />
      ))}

      {/* Batch action buttons for modifying actions */}
      {hasModifyingActions && response.requiresConfirmation && modifyingActions.length > 1 && (
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-700/50">
          <button
            onClick={onDiscardAll}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Discard All
          </button>
          <button
            onClick={onConfirmAll}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/30 transition-colors"
          >
            <Check className="h-3.5 w-3.5" />
            Apply All ({modifyingActions.length})
          </button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const SheetsChat: React.FC<SheetsChatProps> = ({
  prefill,
  onPrefillUsed,
  compact = true,
}) => {
  const navigate = useNavigate();
  const [state, actions] = useSheetsMode();
  const {
    messages,
    isLoading,
    pendingResponse,
    pendingSpec,
    lastCreatedSheet,
    activeSheetContext,
    error,
    hasCapability,
  } = state;
  const {
    sendMessage,
    confirmActions,
    discardActions,
    executeAction,
    skipAction,
    refreshSheetContext,
    confirmSheet,
    discardSheet,
  } = actions;

  const [input, setInput] = React.useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, isLoading, pendingResponse, pendingSpec]);

  // Handle prefill
  useEffect(() => {
    if (prefill && !isLoading && input.trim().length === 0) {
      setInput(prefill);
      onPrefillUsed?.();
    }
  }, [prefill, isLoading, input, onPrefillUsed]);

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;
    const content = input.trim();
    setInput("");
    await sendMessage(content);
  };

  const handleViewSheet = (sheetId: string) => {
    navigate(`/app/workspace/sheets/${sheetId}`);
  };

  // Compute previous roles for avatar visibility
  const previousRoles = messages.map((_, idx) =>
    idx > 0 ? messages[idx - 1]?.role ?? null : null
  );

  const showEmpty = messages.length === 0 && !isLoading;

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    void sendMessage(suggestion);
  };

  // Get loading text based on context
  const getLoadingText = () => {
    if (activeSheetContext) {
      return "Analyzing sheet...";
    }
    return "Designing sheet...";
  };

  // Get placeholder text based on context
  const getPlaceholder = () => {
    if (pendingSpec || pendingResponse) {
      return "Describe changes...";
    }
    if (activeSheetContext) {
      return "Ask about this sheet or request changes...";
    }
    return "Describe a spreadsheet to create...";
  };

  // No API key configured
  if (!hasCapability) {
    return (
      <div className="flex flex-col h-full bg-gray-900">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <AlertCircle className="w-10 h-10 text-amber-400 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">AI Service Unavailable</h3>
          <p className="text-sm text-gray-400 max-w-sm">
            The AI service is not available. Please ensure the backend server is running and configured with an OpenAI API key.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Messages Area */}
      <div ref={scrollRef} className={`flex-1 overflow-y-auto ${showEmpty ? "flex flex-col justify-center" : ""}`}>
        <div className={`mx-auto w-full ${compact ? "px-3 pt-16 pb-4" : "max-w-5xl px-4 md:px-6 pt-28 pb-4"}`}>
          {/* Sheet Context Banner */}
          {activeSheetContext && !showEmpty && (
            <SheetContextBanner
              sheetName={activeSheetContext.name}
              rowCount={activeSheetContext.resolvedData?.preview.length || 0}
              colCount={activeSheetContext.headers.length}
              onRefresh={refreshSheetContext}
            />
          )}

          <AnimatePresence mode="wait">
            {showEmpty ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ChatEmptyState
                  mode="sheets"
                  onSuggestionClick={handleSuggestionClick}
                />
              </motion.div>
            ) : (
              <motion.div
                key="timeline"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-3"
              >
                {messages.map((msg, idx) => (
                  <MessageRow
                    key={msg.id}
                    message={msg}
                    showAvatar={previousRoles[idx] !== msg.role}
                  />
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-gray-400"
                  >
                    <div className="h-7 w-7 rounded-lg bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                      <Table2 className="h-3.5 w-3.5 text-cyan-400" />
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-800/50">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="h-1.5 w-1.5 rounded-full bg-cyan-400"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                        />
                      ))}
                      <span className="ml-1 text-xs text-cyan-400">{getLoadingText()}</span>
                    </div>
                  </motion.div>
                )}

                {/* Pending Response Actions (new two-way system) */}
                {pendingResponse && !isLoading && !pendingSpec && (
                  <div className="ml-9">
                    <PendingActionsCard
                      response={pendingResponse}
                      onConfirmAll={confirmActions}
                      onDiscardAll={discardActions}
                      onExecute={executeAction}
                      onSkip={skipAction}
                    />
                  </div>
                )}

                {/* Sheet Preview Card (legacy CREATE_SHEET) */}
                {pendingSpec && !isLoading && (
                  <div className="ml-9">
                    <SheetPreviewCard
                      spec={pendingSpec}
                      onConfirm={confirmSheet}
                      onDiscard={discardSheet}
                      onViewSheet={handleViewSheet}
                      lastCreatedSheet={lastCreatedSheet}
                    />
                  </div>
                )}

                {/* Created Sheet Card */}
                {lastCreatedSheet && !pendingSpec && !pendingResponse && !isLoading && (
                  <div className="ml-9">
                    <CreatedSheetCard
                      sheetId={lastCreatedSheet.sheetId}
                      sheetName={lastCreatedSheet.sheet.name}
                      onViewSheet={handleViewSheet}
                    />
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="ml-9 flex items-start gap-2 px-3 py-2 bg-rose-500/10 border border-rose-500/30 rounded-lg text-sm text-rose-400"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Composer */}
      <ChatComposer
        value={input}
        onChange={setInput}
        onSubmit={handleSubmit}
        onStop={() => {}}
        isLoading={isLoading}
        mode="sheets"
        onModeChange={() => {}}
        placeholder={getPlaceholder()}
        compact={compact}
      />
    </div>
  );
};

export default SheetsChat;
